import request from 'api/requestUtils'

// ================================
// ENUMS
// ================================

export enum WeddingStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  ARCHIVED = 'ARCHIVED',
}

export enum WeddingLayout {
  CLASSIC = 'CLASSIC',
}

export enum WeddingTheme {
  LIGHT = 'LIGHT',
  DARK = 'DARK',
}

export enum WeddingMediaType {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
}

export enum WeddingGiftType {
  GIFT_CARD = 'GIFT_CARD',
  SHOP = 'SHOP',
}

export enum WeddingGiftStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum WeddingGiftApiCategory {
  HOTEL = 'HOTEL',
  FLIGHT = 'FLIGHT',
  EXPERIENCE = 'EXPERIENCE',
  TOUR = 'TOUR',
  CRUISE = 'CRUISE',
  ANY = 'ANY',
}

export enum WeddingGiftOptionType {
  FIXED = 'FIXED',
  VARIABLE = 'VARIABLE',
}

// ================================
// CONTENT BLOCKS
// ================================

export type WeddingContentBlockType =
  | 'GREETINGS'
  | 'WEDDING_DAY_DETAILS'
  | 'RSVP_FORM'
  | 'GALLERY'
  | 'PUBLIC_MESSAGE_BOARD'
  | 'FAQ'

export type ContentBlockContent = GreetingsContent | WeddingDayContent | RsvpContent | GalleryContent | FaqContent | PublicMessageBoardContent
export interface ContentBlock<T extends ContentBlockContent> {
  type: WeddingContentBlockType
  content?: T
}

export interface PhotoSlot {
  position: number
  imageId: string
}

export interface GreetingsContent {
  message: string
  photos: Array<PhotoSlot>
}

export interface WeddingEventContent {
  venueName: string
  startTime: string
  endTime: string
  note: string
}

export interface WeddingDayContent {
  ceremony: WeddingEventContent
  reception: WeddingEventContent
  dressCode: { code: string; colourPalette: string; note: string }
}

export interface GalleryContent {
  photos: Array<PhotoSlot>
}

export interface FaqItem {
  id: string
  sortOrder: number
  question: string
  answer: string
}

export interface FaqContent {
  items: Array<FaqItem>
}

export interface RsvpContent {
  dietaryRequirements: boolean
  numberOfGuests: boolean
  songRequests: boolean
}

export interface PublicMessageBoardContent {}

// ================================
// TYPES
// ================================

export interface WeddingMedia {
  id: string
  weddingId: string
  type: WeddingMediaType
  imageId: string
  sortOrder: number
}

export interface WeddingMessage {
  id: string
  weddingId: string
  contributionId?: string
  guestId?: string
  authorUserId?: string
  authorFirstName: string
  authorLastName: string
  content: string
  isPublic: boolean
  createdAt: string
}

export interface WeddingQa {
  id: string
  weddingId: string
  question: string
  answer: string
  sortOrder: number
}

export interface WeddingEvent {
  id: string
  weddingId: string
  name?: string
  dressCode?: string
  startDatetime: string
  endDatetime?: string
  locationLabel?: string
  address?: string
  sortOrder: number
}

export interface Wedding {
  id: string
  slug: string
  giftRegistryCartId?: string | null
  title?: string
  status: WeddingStatus
  layout: WeddingLayout
  theme: WeddingTheme
  partnerOneFirstName: string
  partnerOneLastName: string
  partnerTwoFirstName: string
  partnerTwoLastName: string
  mainImageId?: string
  eventStartDate: string
  eventEndDate?: string
  venueAddress?: string
  venuePlaceId?: string
  phoneNumber?: string
  phonePrefix?: string
  notificationEmail: string
  createdByUserId: string
  contentBlocks: Array<ContentBlock<ContentBlockContent>>
}

export interface WeddingGiftCardOption {
  id: string
  type: WeddingGiftOptionType
  amount: number
  currency: string
  sortOrder: number
  isActive: boolean
}

export type WeddingGiftCategory = 'HOTEL' | 'FLIGHT' | 'EXPERIENCE' | 'CRUISE' | 'TOUR' | 'ANY'

export interface WeddingGiftContributions {
  progress: number
  contributorsCount: number
}

export interface WeddingGift {
  id: string
  type: WeddingGiftType
  title: string
  imageId: string
  category: WeddingGiftCategory
  amount: number
  currency: string
  contributions?: WeddingGiftContributions
}

// ================================
// API RESPONSE TYPES
// ================================

interface ApiResponse<T> {
  statusCode: number
  result: T
}

// ================================
// API FUNCTIONS
// ================================

export async function getWeddingBySlug(slug: string): Promise<Wedding> {
  const response = await request.get<ApiResponse<Wedding>>(
    `/api/occasions/v1/weddings/slug/${slug}`,
  )
  return response.result
}

export interface PaginatedResponse<T> {
  data: Array<T>
  pagination: {
    total: number
    limit: number
    offset: number
  }
}

export interface GetWeddingMessagesParams {
  limit?: number
  offset?: number
}

export async function getWeddingMessages(
  weddingId: string,
  params: GetWeddingMessagesParams = {},
): Promise<PaginatedResponse<WeddingMessage>> {
  const queryParams = new URLSearchParams()

  if (params.limit !== undefined) {
    queryParams.set('limit', params.limit.toString())
  }
  if (params.offset !== undefined) {
    queryParams.set('offset', params.offset.toString())
  }

  const queryString = queryParams.toString()
  const response = await request.get<ApiResponse<PaginatedResponse<WeddingMessage>>>(
    `/api/occasions/v1/weddings/${weddingId}/messages${queryString ? `?${queryString}` : ''}`,
  )
  return response.result
}

export async function createWeddingMessage(
  weddingId: string,
  payload: {
    authorFirstName: string
    authorLastName: string
    content: string
    contributionId?: string
    guestId?: string
  },
): Promise<WeddingMessage> {
  const response = await request.post<ApiResponse<WeddingMessage>, typeof payload>(
    `/api/occasions/v1/weddings/${weddingId}/messages`,
    payload,
    { credentials: 'include' },
  )
  return response.result
}

// ================================
// WEDDING CRUD
// ================================

export interface CreateWeddingPayload {
  slug?: string
  title?: string
  status?: WeddingStatus
  partnerOneFirstName: string
  partnerOneLastName?: string
  partnerTwoFirstName: string
  partnerTwoLastName?: string
  mainImageId?: string
  eventStartDate: string
  eventEndDate?: string
  venueAddress?: string
  notificationEmail: string
}

export async function createWedding(payload: CreateWeddingPayload): Promise<Wedding> {
  const response = await request.post<ApiResponse<Wedding>, CreateWeddingPayload>(
    '/api/occasions/v1/weddings',
    payload,
    { credentials: 'include' },
  )
  return response.result
}

export interface UpdateWeddingPayload {
  slug?: string
  title?: string
  status?: WeddingStatus
  partnerOneFirstName?: string
  partnerOneLastName?: string
  partnerTwoFirstName?: string
  partnerTwoLastName?: string
  mainImageId?: string
  eventStartDate?: string
  eventEndDate?: string
  venueAddress?: string
  notificationEmail?: string
}

export async function updateWedding(weddingId: string, payload: UpdateWeddingPayload): Promise<Wedding> {
  const response = await request.put<ApiResponse<Wedding>, UpdateWeddingPayload>(
    `/api/occasions/v1/weddings/${weddingId}`,
    payload,
    { credentials: 'include' },
  )
  return response.result
}

export async function ensureWeddingGiftRegistryCart(weddingId: string): Promise<Wedding> {
  const response = await request.post<ApiResponse<Wedding>, Record<string, never>>(
    `/api/occasions/v1/weddings/${weddingId}/gift-registry-cart`,
    {},
    { credentials: 'include' },
  )
  return response.result
}

// ================================
// IMAGE UPLOAD
// ================================

interface UploadImageResponse {
  imageId: string
}

export async function uploadImage(file: File): Promise<UploadImageResponse> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await request.upload<ApiResponse<UploadImageResponse>>(
    {
      path: '/api/occasions/v1/images/upload',
      data: formData,
      options: { credentials: 'include' },
    },
  )

  return response.result
}

// ================================
// SLUG VALIDATION
// ================================

export async function verifyWeddingSlug(slug: string, excludeId?: string): Promise<boolean> {
  const params = new URLSearchParams({ slug })
  if (excludeId) params.set('excludeId', excludeId)
  const response = await request.get<ApiResponse<{ available: boolean }>>(
    `/api/occasions/v1/weddings/verify-slug?${params.toString()}`,
    { credentials: 'include' },
  )
  return response.result.available
}

// ================================
// LEAD SUBMISSION
// ================================

export async function submitWeddingLead(): Promise<void> {
  await request.post('/api/occasions/v1/weddings/lead', {}, { credentials: 'include' })
}

// ================================
// WEDDING GIFTS
// ================================

export async function getWeddingGifts(weddingId: string): Promise<Array<WeddingGift>> {
  const response = await request.get<ApiResponse<Array<WeddingGift>>>(
    `/api/occasions/v1/weddings/${weddingId}/gifts`,
  )
  return response.result
}

export interface CreateWeddingGiftPayload {
  type?: WeddingGiftType
  title: string
  imageId: string
  category: WeddingGiftCategory
  amount: number
  currency: string
}

export async function createWeddingGift(
  weddingId: string,
  payload: CreateWeddingGiftPayload,
): Promise<WeddingGift> {
  let type = payload.type
  if (!type) {
    type = WeddingGiftType.GIFT_CARD
  }

  const response = await request.post<ApiResponse<WeddingGift>, CreateWeddingGiftPayload & { type: string }>(
    `/api/occasions/v1/weddings/${weddingId}/gifts`,
    { ...payload, type },
    { credentials: 'include' },
  )
  return response.result
}

export interface UpdateWeddingGiftPayload {
  status?: 'ACTIVE' | 'INACTIVE'
}

export async function updateWeddingGift(
  weddingId: string,
  giftId: string,
  payload: UpdateWeddingGiftPayload,
): Promise<void> {
  await request.put<ApiResponse<WeddingGift>, UpdateWeddingGiftPayload>(
    `/api/occasions/v1/weddings/${weddingId}/gifts/${giftId}`,
    payload,
    { credentials: 'include' },
  )
}

// ================================
// WEDDING GUESTS
// ================================

export enum WeddingRsvpStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  DECLINED = 'DECLINED',
}

export interface WeddingGuest {
  id: string
  weddingId: string
  userId?: string
  firstName: string
  lastName: string
  role: string
  additionalGuestCount: number
  rsvpStatus: WeddingRsvpStatus
  repliedAt?: string
  dietaryNeeds?: string
  songRequest?: string
  invitedAt?: string
}

export interface CreateWeddingGuestPayload {
  firstName: string
  lastName: string
  additionalGuestCount?: number
  rsvpStatus?: WeddingRsvpStatus
  dietaryNeeds?: string
  songRequest?: string
}

export async function createWeddingGuest(
  weddingId: string,
  payload: CreateWeddingGuestPayload,
): Promise<WeddingGuest> {
  const response = await request.post<ApiResponse<WeddingGuest>, CreateWeddingGuestPayload>(
    `/api/occasions/v1/weddings/${weddingId}/guests`,
    payload,
    { credentials: 'include' },
  )
  return response.result
}
